// `/nueva-contrasena?token_hash=…&type=recovery` — el final del «olvidé mi clave».
//
// ⭐ POR QUÉ EL ENLACE LO ARMAMOS NOSOTROS Y NO SUPABASE. La plantilla de
// recuperación del proyecto construye el botón con `{{ .SiteURL }}` a pelo —
// nunca `.ConfirmationURL`— así que ignora cualquier `redirectTo` y manda
// SIEMPRE a work.victorix-ai.com. Y esa plantilla la comparten los seis
// productos: tocarla es meterle mano al login de Victorix Work, que tiene gente
// encima. La salida es `generateLink`, que devuelve el token SIN mandar correo:
// el servidor arma este enlace y esta pantalla lo canjea con `verifyOtp`.
// El token lo sigue emitiendo Supabase — nadie reinventa criptografía.
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Aviso, Boton } from "@victorix/chat-ai-ui";
import { sb } from "../lib/supabase.ts";
import { AvisoCuentaUnica, CampoClave, Estado, MarcoAuth } from "./_marco.tsx";

type Fase = "canjeando" | "muerto" | "listo" | "cambiada";

export default function NuevaContrasena() {
  const [params] = useSearchParams();
  const [fase, setFase] = useState<Fase>("canjeando");
  const [clave, setClave] = useState("");
  const [repetir, setRepetir] = useState("");
  const [ver, setVer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const tokenHash = params.get("token_hash");

  // El canje abre una sesión temporal: sin ella `updateUser` no tendría a quién
  // cambiarle la clave. Va en un efecto y una sola vez — canjear dos veces el
  // mismo token falla, y el usuario vería «enlace vencido» sin haber hecho nada.
  useEffect(() => {
    let vivo = true;
    if (!tokenHash) { setFase("muerto"); return; }
    sb.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" }).then(({ error }) => {
      if (!vivo) return;
      setFase(error ? "muerto" : "listo");
    });
    return () => { vivo = false; };
  }, [tokenHash]);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (clave !== repetir) { setError("Las dos contraseñas no son iguales."); return; }
    setGuardando(true); setError(null);
    const { error } = await sb.auth.updateUser({ password: clave });
    if (error) {
      // Supabase rechaza aquí las contraseñas filtradas (HIBP está activo en el
      // proyecto). Se dice con palabras, no con el error crudo en inglés.
      setError(/pwned|breach|compromis/i.test(error.message)
        ? "Esa contraseña aparece en filtraciones conocidas. Elige otra."
        : error.message);
      setGuardando(false);
      return;
    }
    setFase("cambiada");
  }

  if (fase === "canjeando") {
    return <MarcoAuth titulo="Un momento…"><div className="skeleton h-11 w-full rounded-lg" /></MarcoAuth>;
  }

  if (fase === "muerto") {
    return (
      <Estado
        titulo="Este enlace ya no sirve"
        accion={<Link to="/recuperar"><Boton tono="marca" className="w-full">Pedir uno nuevo</Boton></Link>}
      >
        <p>Los enlaces para cambiar la contraseña vencen en 1 hora y solo funcionan una vez.</p>
      </Estado>
    );
  }

  if (fase === "cambiada") {
    return (
      <Estado
        titulo="Listo, ya puedes entrar"
        accion={<Link to="/"><Boton tono="marca" className="w-full">Ir al panel</Boton></Link>}
      >
        <p>Tu contraseña quedó cambiada.</p>
        <p className="text-tinta">
          Recuerda que es la misma con la que entras a <b>Victorix Work</b>.
        </p>
      </Estado>
    );
  }

  return (
    <MarcoAuth titulo="Crea tu contraseña nueva">
      <form onSubmit={guardar}>
        <div className="space-y-4">
          <CampoClave
            id="clave" etiqueta="Contraseña nueva" autoComplete="new-password"
            valor={clave} alCambiar={setClave} ver={ver} alVer={setVer}
            ayuda="Mínimo 8 caracteres."
          />
          <CampoClave
            id="repetir" etiqueta="Repítela" autoComplete="new-password"
            valor={repetir} alCambiar={setRepetir} ver={ver} alVer={setVer}
          />
        </div>

        <AvisoCuentaUnica />
        {error && <div className="mt-4"><Aviso tono="peligro">{error}</Aviso></div>}

        <Boton type="submit" tono="marca" className="mt-6 w-full"
               disabled={guardando || clave.length < 8 || !repetir}>
          {guardando ? "Guardando…" : "Guardar contraseña"}
        </Boton>
      </form>
    </MarcoAuth>
  );
}
